import KatalLogger from '@amzn/katal-logger';

const CSP_VIOLATION_MESSAGE = 'Content Security Policy Violation detected';
// Common blocked URIs that should always be ignored
const BLOCKED_URI_FILTERS = [
  'is-it-down', // Generally just related to style-src violations (do not impact availability or security)
  'fonts.gstatic.com', // Google Fonts requests from browser extensions, not critical for functionality
  'cdn.prod.badgephotos.side.amazon.dev', // Violations due to auth redirects, non-critical for Task Manager
];

export const MAX_REPORTS_PER_KEY = 3;

/**
 * Handles CSP violations by logging them
 * @param {SecurityPolicyViolationEvent} event - The CSP violation event
 * @param {KatalLogger} cspLogger - The logger instance
 * @param {Map<string, number>} seen - Deduplication map tracking violation counts per unique key
 * @param {string[]} sourceFileFilters - Optional array of strings to ignore violations with sourceFile containing these patterns
 */
function handleSecurityPolicyViolation(
  event: SecurityPolicyViolationEvent,
  cspLogger: KatalLogger,
  seen: Map<string, number>,
  sourceFileFilters: string[] = []
) {
  // Check if violation should be filtered
  let isFiltered = false;
  if (sourceFileFilters.length > 0 && event.sourceFile) {
    isFiltered = sourceFileFilters.some((pattern) => event.sourceFile!.includes(pattern));
  }

  // Check if blockedURI should be filtered against common patterns that should be ignored
  if (!isFiltered) {
    isFiltered = BLOCKED_URI_FILTERS.some((pattern) => event.blockedURI.includes(pattern));
  }

  // Deduplicate using separate budgets for filtered vs actionable violations
  const dedupeKey = `${isFiltered ? 'filtered|' : ''}${event.violatedDirective}|${event.blockedURI}`;
  const count = seen.get(dedupeKey) ?? 0;
  if (count >= MAX_REPORTS_PER_KEY) return;
  seen.set(dedupeKey, count + 1);

  const details: Record<string, string> = {
    blockedURI: event.blockedURI,
    disposition: event.disposition,
    documentURI: event.documentURI,
    effectiveDirective: event.effectiveDirective,
    lineNumber: String(event.lineNumber),
    columnNumber: String(event.columnNumber),
    referrer: event.referrer,
    sample: event.sample,
    sourceFile: event.sourceFile,
    statusCode: String(event.statusCode),
    violatedDirective: event.violatedDirective,
  };

  const { pathname, hostname, href } = window.location;
  const logContext = {
    ...details,
    timestamp: new Date().getTime(),
    pathname,
    hostname,
    href,
    ignore: isFiltered ? 'true' : 'false',
  };
  const errorMessage = `CSP violation: ${event.violatedDirective} blocked ${event.blockedURI}`;
  /**
   * Log Content Security Policy (CSP) violations at the "warn" level.
   *
   * CSP violations can occur due to invalid directives, extensions, or other non-fatal errors.
   * These violations should be analyzed separately from more serious fatal or error-level logs,
   * as they may not necessarily indicate critical issues.
   */
  cspLogger.warn(CSP_VIOLATION_MESSAGE, new Error(errorMessage), logContext);
}

/**
 * Initializes the CSP violation handler
 * @param {KatalLogger} cspLogger - The logger instance
 * @param {string[]} sourceFileFilters - Optional array of strings to filter out violations with sourceFile containing these patterns
 */
export function initCSPViolationHandler(cspLogger: KatalLogger, sourceFileFilters: string[] = []): () => void {
  const seen = new Map<string, number>();
  const handler = (event: SecurityPolicyViolationEvent) =>
    handleSecurityPolicyViolation(event, cspLogger, seen, sourceFileFilters);

  document.addEventListener('securitypolicyviolation', handler);

  return () => document.removeEventListener('securitypolicyviolation', handler);
}
